import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../../App';
import { Building2, Calendar, MapPin, Users, Clock, ArrowLeft, CheckCircle, XCircle, AlertTriangle, User, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate, formatTimeRange } from '../../utils/dateTime';

const PartnerOpportunityDetail = () => {
  const { id } = useParams();
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(null);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgSuspended, setOrgSuspended] = useState(false);

  const fetchOpportunity = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/opportunities/${id}`);
      setOpportunity(response.data);
    } catch (error) {
      toast.error('Failed to load opportunity');
      navigate('/partner/opportunities');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchSignups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/opportunities/${id}/signups`);
      setSignups(response.data || []);
    } catch (err) {
      console.error('fetchSignups failed', err);
    }
  }, [id]);

  useEffect(() => {
    fetchOpportunity();
    fetchSignups();
  }, [fetchOpportunity, fetchSignups]);

  const handleApproveSignup = async (signupId, status) => {
    if (orgSuspended) {
      toast.error('Organization is suspended. Cannot modify signups.');
      return;
    }
    
    try {
      await axios.put(`${API}/signups/${signupId}/approve`, { status });
      toast.success(`Signup ${status}`);
      fetchSignups();
      fetchOpportunity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update signup');
    }
  };

  const handleMarkAttendance = async (signupId, status) => {
    if (orgSuspended) {
      toast.error('Organization is suspended. Cannot mark attendance.');
      return;
    }
    
    try {
      await axios.put(`${API}/signups/${signupId}/attendance`, { status });
      toast.success(`Attendance marked as ${status}`);
      fetchSignups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF4EA] flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!opportunity) {
    return null;
  }

  const oppDate = parseLocalDate(opportunity.date);
  const formattedDate = format(oppDate, 'EEEE, MMMM d, yyyy');
  const isPast = oppDate < new Date();
  
  const pendingSignups = signups.filter(s => s.status === 'pending');
  const approvedSignups = signups.filter(s => s.status === 'approved');
  const attendedSignups = signups.filter(s => s.status === 'attended');
  const noShows = signups.filter(s => s.status === 'no_show');

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="partner-opportunity-detail">
      {/* Navigation */}
      <nav className="bg-white border-b-2 border-[#E9DDCF]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#8B2E3C]" />
              <div>
                <div className="font-bold text-lg text-[#222222]">{user?.partner_org_name || 'Partner Portal'}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/partner/dashboard" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Dashboard</Link>
              <Link to="/partner/opportunities" className="text-[#8B2E3C] font-bold">Opportunities</Link>
              <Link to="/partner/community" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Community Board</Link>
              <Link to="/partner/participants" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Participants</Link>
              <Link to="/partner/invites" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Invites</Link>
              <Link to="/partner/staff" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Staff</Link>
              <Link to="/partner/time-entries" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Time Entries</Link>
              <Link to="/partner/reports" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Reports</Link>
              <button onClick={logout} className="text-[#8B2E3C] hover:underline text-sm">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Suspended Banner */}
      {orgSuspended && (
        <div className="bg-red-100 border-b-2 border-red-300 py-3">
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-700" />
            <span className="text-red-800 font-medium">Organization suspended — contact admin. View-only mode.</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Link */}
        <button 
          onClick={() => navigate('/partner/opportunities')} 
          className="flex items-center gap-2 text-[#8B2E3C] hover:underline mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Opportunities
        </button>

        {/* Opportunity Header */}
        <div className="card mb-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-[#222222] mb-4">{opportunity.title}</h1>
              
              <div className="grid sm:grid-cols-2 gap-4 text-sm text-[#666666] mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#8B2E3C]" />
                  <span>{formattedDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#8B2E3C]" />
                  <span>{formatTimeRange(opportunity.start_time, opportunity.end_time)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#8B2E3C]" />
                  <span>{opportunity.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#8B2E3C]" />
                  <span>{approvedSignups.length + attendedSignups.length} / {opportunity.capacity} filled</span>
                </div>
              </div>
              
              <p className="text-[#666666]">{opportunity.description}</p>
              
              {opportunity.host_contact && (
                <p className="mt-4 text-sm">
                  <span className="font-medium">Supervisor:</span> {opportunity.host_contact}
                </p>
              )}
            </div>
            
            {/* Stats Summary */}
            <div className="grid grid-cols-2 gap-4 lg:w-64">
              <div className="bg-[#FFF9F0] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-[#C9A227]">{pendingSignups.length}</div>
                <div className="text-xs text-[#666666]">Pending</div>
              </div>
              <div className="bg-[#F0FFF4] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{approvedSignups.length}</div>
                <div className="text-xs text-[#666666]">Approved</div>
              </div>
              <div className="bg-[#F0F9FF] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{attendedSignups.length}</div>
                <div className="text-xs text-[#666666]">Attended</div>
              </div>
              <div className="bg-[#FFF0F0] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{noShows.length}</div>
                <div className="text-xs text-[#666666]">No-Shows</div>
              </div>
            </div>
          </div>
        </div>

        {/* Close / Reopen Opportunity */}
        {user?.role === 'partner_admin' && (
          <div className="mb-6 flex gap-3">
            {opportunity.status !== 'closed' ? (
              <button
                onClick={async () => {
                  try {
                    await axios.put(`${API}/opportunities/${id}/close`);
                    toast.success('Opportunity closed');
                    fetchOpportunity();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to close');
                  }
                }}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
                data-testid="close-opportunity-button"
              >
                <Lock className="w-4 h-4" /> Close Opportunity
              </button>
            ) : (
              <button
                onClick={async () => {
                  try {
                    await axios.put(`${API}/opportunities/${id}/reopen`);
                    toast.success('Opportunity reopened');
                    fetchOpportunity();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to reopen');
                  }
                }}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
                data-testid="reopen-opportunity-button"
              >
                <Unlock className="w-4 h-4" /> Reopen Opportunity
              </button>
            )}
            {opportunity.status === 'closed' && (
              <span className="flex items-center gap-1 text-red-600 font-medium text-sm">
                <Lock className="w-4 h-4" /> This opportunity is closed
              </span>
            )}
          </div>
        )}

        {/* Pending Approvals */}
        {pendingSignups.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#222222] mb-4">Pending Approvals ({pendingSignups.length})</h2>
            <div className="grid gap-3">
              {pendingSignups.map(signup => (
                <div key={signup.id} className="card bg-[#FFF9F0]" data-testid={`pending-signup-${signup.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#E9DDCF] rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-[#666666]" />
                      </div>
                      <div>
                        <div className="font-medium text-[#222222]">{signup.user_name}</div>
                        <div className="text-sm text-[#666666]">{signup.user_email}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApproveSignup(signup.id, 'approved')}
                        className="btn-primary flex items-center gap-1 text-sm py-1 px-3"
                        disabled={orgSuspended}
                        data-testid={`approve-btn-${signup.id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleApproveSignup(signup.id, 'declined')}
                        className="btn-outline flex items-center gap-1 text-sm py-1 px-3 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                        disabled={orgSuspended}
                        data-testid={`decline-btn-${signup.id}`}
                      >
                        <XCircle className="w-4 h-4" />
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approved Signups (for marking attendance) */}
        {isPast && approvedSignups.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#222222] mb-4">Mark Attendance ({approvedSignups.length})</h2>
            <div className="grid gap-3">
              {approvedSignups.map(signup => (
                <div key={signup.id} className="card" data-testid={`approved-signup-${signup.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#E9DDCF] rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-[#666666]" />
                      </div>
                      <div>
                        <div className="font-medium text-[#222222]">{signup.user_name}</div>
                        <div className="text-sm text-[#666666]">{signup.user_email}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleMarkAttendance(signup.id, 'attended')}
                        className="bg-green-600 text-white rounded-lg flex items-center gap-1 text-sm py-2 px-3 hover:bg-green-700"
                        disabled={orgSuspended}
                        data-testid={`attended-btn-${signup.id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Attended
                      </button>
                      <button
                        onClick={() => handleMarkAttendance(signup.id, 'no_show')}
                        className="bg-red-600 text-white rounded-lg flex items-center gap-1 text-sm py-2 px-3 hover:bg-red-700"
                        disabled={orgSuspended}
                        data-testid={`noshow-btn-${signup.id}`}
                      >
                        <XCircle className="w-4 h-4" />
                        No-Show
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Approved (before event date) */}
        {!isPast && approvedSignups.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#222222] mb-4">Confirmed Volunteers ({approvedSignups.length})</h2>
            <div className="grid gap-3">
              {approvedSignups.map(signup => (
                <div key={signup.id} className="card" data-testid={`confirmed-signup-${signup.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#E9DDCF] rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-[#666666]" />
                    </div>
                    <div>
                      <div className="font-medium text-[#222222]">{signup.user_name}</div>
                      <div className="text-sm text-[#666666]">{signup.user_email}</div>
                    </div>
                    <span className="badge badge-approved ml-auto">Confirmed</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Attendance */}
        {(attendedSignups.length > 0 || noShows.length > 0) && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-[#222222] mb-4">Attendance Record</h2>
            <div className="grid gap-3">
              {attendedSignups.map(signup => (
                <div key={signup.id} className="card bg-green-50" data-testid={`attended-record-${signup.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-[#222222]">{signup.user_name}</div>
                      <div className="text-sm text-[#666666]">{signup.user_email}</div>
                    </div>
                    <div className="ml-auto text-right">
                      <span className="badge bg-green-100 text-green-700">Attended</span>
                      {signup.hours_credited > 0 && (
                        <div className="text-xs text-green-600 mt-1">{signup.hours_credited}h credited</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {noShows.map(signup => (
                <div key={signup.id} className="card bg-red-50" data-testid={`noshow-record-${signup.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <div className="font-medium text-[#222222]">{signup.user_name}</div>
                      <div className="text-sm text-[#666666]">{signup.user_email}</div>
                    </div>
                    <span className="badge bg-red-100 text-red-700 ml-auto">No-Show</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {signups.length === 0 && (
          <div className="card text-center py-12">
            <Users className="w-12 h-12 text-[#E9DDCF] mx-auto mb-4" />
            <p className="text-[#666666]">No signups yet for this opportunity</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerOpportunityDetail;
